import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Close, Delete, CalendarMonth, Work, Mail } from "@mui/icons-material";

import GoogleCalendarDialogPublic from "../../pages/integration/GoogleCalendarDialogPublic";
import WorkizDialogPublic from "../../pages/integration/WorkizDialogPublic";
import chatwidgetService from "../../services/chatwidget.service";
import ActionsService from "../../services/actions.service";
import PublicEmailLeads from "../../pages/agent/PublicEmailLeads";

const ChatHeader = ({ webDetail, toggleChat, onClearChat, messages }) => {
  const [googleDialogOpen, setGoogleDialogOpen] = useState(false);
  const [workizDialogOpen, setWorkizDialogOpen] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [connections, setConnections] = useState({
    google: false,
    workiz: false,
  });

  const userId = webDetail?.websiteDetails?.user?.id;

  const [emailLeadsOpen, setEmailLeadsOpen] = useState(false);

  // ✅ Check session
  const checkSession = useCallback(() => {
    try {
      const session = localStorage.getItem("userSession");
      return !!JSON.parse(session)?.user;
    } catch {
      return false;
    }
  }, []);

  // ✅ Fetch connections
  const checkConnections = useCallback(async () => {
    if (!userId) return;
    try {
      const [googleRes, workizRes] = await Promise.all([
        chatwidgetService.getConnection(userId),
        ActionsService.WorkizgetConnection(userId),
      ]);

      setConnections({
        google: googleRes?.success && !!googleRes?.data?.accessToken,
        workiz: !!(workizRes?.apiKey && workizRes?.apiSecret),
      });
    } catch (err) {
      console.error("❌ Connection check error:", err);
      setConnections({ google: false, workiz: false });
    }
  }, [userId]);

  // ✅ Poll localStorage for session until it exists
  useEffect(() => {
    let interval;

    const startPolling = () => {
      interval = setInterval(() => {
        const sessionExists = checkSession();
        if (sessionExists) {
          setHasSession(true);
          if (userId) checkConnections();
          clearInterval(interval);
        }
      }, 300); // check every 300ms
    };

    startPolling();

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [checkSession, checkConnections, userId]);

  const icons = useMemo(
    () => (
      <>
        {messages?.length > 0 && (
          <IconButton onClick={onClearChat} sx={{ bgcolor: "#e0e0e0" }}>
            <Delete sx={{ color: "black" }} />
          </IconButton>
        )}

        {hasSession && (
          <Tooltip title="Customer Emails" arrow>
            <IconButton
              onClick={() => setEmailLeadsOpen(true)}
              sx={{ bgcolor: "#e0e0e0" }}
            >
              <Mail sx={{ color: "black" }} />
            </IconButton>
          </Tooltip>
        )}

        {hasSession && connections.google && (
          <Tooltip title="Google Calendar">
            <IconButton
              onClick={() => setGoogleDialogOpen(true)}
              sx={{ bgcolor: "#e0e0e0" }}
            >
              <CalendarMonth sx={{ color: "black" }} />
            </IconButton>
          </Tooltip>
        )}

        {hasSession && connections.workiz && (
          <Tooltip title="Workiz Jobs" arrow>
            <IconButton
              onClick={() => setWorkizDialogOpen(true)}
              sx={{ bgcolor: "#e0e0e0" }}
            >
              <Work sx={{ color: "black" }} />
            </IconButton>
          </Tooltip>
        )}

        <IconButton onClick={toggleChat} sx={{ bgcolor: "#e0e0e0" }}>
          <Close sx={{ color: "black" }} />
        </IconButton>
      </>
    ),
    [messages, hasSession, connections, onClearChat, toggleChat]
  );

  return (
    <>
      <Box className="chat-header">
        <Typography variant="h6">
          {webDetail?.websiteDetails?.title || "Welcome"}
          <br />
          <Typography
            variant="subtitle1"
            component="span"
            color="text.secondary"
          >
            {webDetail?.websiteDetails?.subtitle}
          </Typography>
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>{icons}</Box>
      </Box>

      {hasSession && connections.google && (
        <GoogleCalendarDialogPublic
          open={googleDialogOpen}
          onClose={() => setGoogleDialogOpen(false)}
          webDetail={webDetail}
        />
      )}

      {hasSession && connections.workiz && (
        <WorkizDialogPublic
          open={workizDialogOpen}
          onClose={() => setWorkizDialogOpen(false)}
          webDetail={webDetail}
        />
      )}

      {hasSession && (
        <PublicEmailLeads
          open={emailLeadsOpen}
          onClose={() => setEmailLeadsOpen(false)}
        />
      )}
    </>
  );
};

export default ChatHeader;
